import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { CompanyList } from './CompanyList';
import { CompanyForm } from './CompanyForm';
import { EnqueriesModal } from './EnqueriesModal';
import CompanyDetails from './CompanyDetails';
import api from '../../utils/api';

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEnqueriesModalVisible, setIsEnqueriesModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pendingEnqueries, setPendingEnqueries] = useState(0);
  const { t } = useTranslation();
  const [toast, setToast] = useState({ visible: false, severity: '', summary: '', detail: '' });

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {}
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
    loadPendingEnqueries();
  }, [lazyParams]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/companies', { params: lazyParams });
      setCompanies(response.data.data);
      setTotalRecords(response.data.total);
    } catch (error) {
      console.error('Error loading companies:', error);
      showToast('error', t('error'), t('error_loading_companies'));
    } finally {
      setLoading(false);
    }
  };

  const loadPendingEnqueries = async () => {
    try {
      const response = await api.get('/companies/enqueries/count');
      setPendingEnqueries(response.data);
    } catch (error) {
      console.error('Error loading pending enqueries:', error);
      showToast('error', t('error'), t('error_loading_pending_enqueries'));
    }
  };

  const showToast = (severity, summary, detail) => {
    setToast({ visible: true, severity, summary, detail });
    setTimeout(() => {
      setToast({ visible: false, severity: '', summary: '', detail: '' });
    }, 3000);
  };

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setIsFormVisible(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setIsFormVisible(true);
  };

  const handleViewCompany = (company) => {
    navigate(`/companies/${company.id}`);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      loadCompanies();
      showToast('success', t('success'), t('company_deleted_successfully'));
    } catch (error) {
      console.error('Error deleting company:', error);
      showToast('error', t('error'), t('error_deleting_company'));
    }
  };

  const handleSaveCompany = async (formData) => {
    try {
      if (selectedCompany) {
        formData.append('_method', 'PUT');
        await api.post(`/companies/${selectedCompany.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.post('/companies', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      loadCompanies();
      setIsFormVisible(false);
      showToast('success', t('success'), t('company_saved_successfully'));
    } catch (error) {
      console.error('Error saving company:', error);
      showToast('error', t('error'), t('error_saving_company'));
    }
  };

  const handleEnqueriesClick = () => {
    setIsEnqueriesModalVisible(true);
  };

  return (
    <div className="companies-page p-4">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <h3 className="font-bold">{toast.summary}</h3>
          <p>{toast.detail}</p>
        </div>
      )}

      <Routes>
        <Route path="/" element={
          <>
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold">{t('companies')}</h1>
              <button
                onClick={handleEnqueriesClick}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
              >
                {t('pending_enqueries')}
                {pendingEnqueries > 0 && (
                  <span className="ml-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs">
                    {pendingEnqueries}
                  </span>
                )}
              </button>
            </div>

            <CompanyList
              companies={companies}
              loading={loading}
              totalRecords={totalRecords}
              lazyParams={lazyParams}
              setLazyParams={setLazyParams}
              onCreateCompany={handleCreateCompany}
              onEditCompany={handleEditCompany}
              onViewCompany={handleViewCompany}
              onDeleteCompany={handleDeleteCompany}
            />

            <CompanyForm
              visible={isFormVisible}
              onHide={() => setIsFormVisible(false)}
              onSave={handleSaveCompany}
              company={selectedCompany}
            />

            <EnqueriesModal
              visible={isEnqueriesModalVisible}
              onHide={() => setIsEnqueriesModalVisible(false)}
              onEnqueryMarkedAsSeen={loadPendingEnqueries}
            />
          </>
        } />
        <Route path=":id" element={<CompanyDetails />} />
      </Routes>
    </div>
  );
}

